import * as React from "react";
import * as ReactDOM from "react-dom";
import App from "./App";
import "./styles/index.css";
import security from "./modules/common/services/SecurityService";
import "react-virtualized-select/node_modules/react-select/dist/react-select.css";
import "react-virtualized-select/styles.css";
import "@ant-design/flowchart/dist/index.css";
import UserInfoService from "./modules/common/services/UserInfoService";
import { AuthProvider } from "./modules/authorization/AuthContext";
import NoRolesError from "./modules/common/types/CustomErrors";
import configurationManager from "./modules/common/services/ConfigurationManager";
import { ConfigProvider } from "./modules/common/contexts/ConfigContext";
import {
    BootstrapConfig,
    RuntimeConfig,
    WorkbenchConfig,
} from "./modules/common/types/Configs";

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        require: (val: string) => any;
        RUNTIME_CONFIG: {
            [key: string]: unknown;
        };
    }
}

const renderApp = (
    configState: {
        bootstrapConfig: BootstrapConfig | null;
        runtimeConfig: RuntimeConfig;
        workbenchConfig: WorkbenchConfig | null;
        isLoading: boolean;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userInfo: any,
) => {
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(
        <ConfigProvider value={configState}>
            <AuthProvider
                value={{
                    permissions: userInfo!.permissions,
                    objectPermissions: userInfo!.objectPermissions,
                    projectRoles: userInfo.projectRoles,
                }}
            >
                <App />
            </AuthProvider>
        </ConfigProvider>,
        document.getElementById("root") as HTMLElement,
    );
};

const renderError = (error: Error) => {
    if (error instanceof NoRolesError) {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.render(
            (error as NoRolesError).errorScreen,
            document.getElementById("root") as HTMLElement,
        );
    } else {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.render(
            <div>Application failed to initialize: {error.message}</div>,
            document.getElementById("root") as HTMLElement,
        );
    }
};

const initApp = async () => {
    try {
        // Step 1: Load bootstrap config from RUNTIME_CONFIG
        const bootstrapConfig = configurationManager.loadBootstrapConfig();
        console.log("Bootstrap config loaded:", bootstrapConfig);

        // Step 2: Configure security service with keycloak overrides
        security.configure(bootstrapConfig?.keycloakConfig);

        // Step 3: Initialize security/authentication
        await security.init();
        console.log("Security initialized");

        // Step 4: Load runtime configuration from backend
        const runtimeConfig = await configurationManager.loadRuntimeConfig();
        console.log("Runtime config loaded:", runtimeConfig);

        // Step 5: Load workbench configuration only if workbench is enabled
        let workbenchConfig = null;
        if (runtimeConfig.workbenchEnabled) {
            workbenchConfig = await configurationManager.loadWorkbenchConfig();
            console.log("Workbench config loaded:", workbenchConfig);
        }

        // Step 6: Get user info
        const userInfo = await UserInfoService.getUserInfo();
        console.log("User info loaded:", userInfo);

        // Step 7: Prepare config state for context
        const configState = {
            bootstrapConfig,
            runtimeConfig,
            workbenchConfig,
            isLoading: false,
        };

        // Step 8: Render app with all configurations
        renderApp(configState, userInfo);
    } catch (error) {
        console.error("App initialization failed:", error);
        renderError(error);
    }
};

if (module.hot) {
    module.hot.accept(["./App"], () => {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const NextApp = require("./App").default;
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.render(
            <NextApp />,
            document.getElementById("root") as HTMLElement,
        );
    });
}

initApp();
